import { AsyncPipe, NgIf } from '@angular/common';
import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { BoondockFacade } from '@curbnturf/boondock-listing';
import { Boondock, BoondockRating, RatingType, Schedule, User } from '@curbnturf/entities';
import { IBoondockRatingForm } from '@curbnturf/form-helpers';
import { RatingActionTypes, RatingFacade } from '@curbnturf/rating';
import { ScheduleFacade } from '@curbnturf/schedule/src/lib/+state/schedule.facade';
import { UserFacade } from '@curbnturf/user/src/lib/+state/user.facade';
import { IonicModule, NavController } from '@ionic/angular';
import { Actions, ofType } from '@ngrx/effects';
import { DateTime } from 'luxon';
import { Observable, Subscription } from 'rxjs';
import { RatingPillComponent } from '../rating-pill/rating-pill.component';
import { RatingRateComponent } from '../rating-rate/rating-rate.component';

@Component({
  selector: 'curbnturf-rating-boondock-sections',
  templateUrl: './rating-boondock-sections.component.html',
  styleUrls: ['./rating-boondock-sections.component.scss'],
  standalone: true,
  imports: [
    AsyncPipe,
    FormsModule,
    IonicModule,
    NgIf,
    RatingPillComponent,
    RatingRateComponent,
    ReactiveFormsModule,
    RouterModule,
  ],
})
export class RatingBoondockSectionsComponent implements OnInit, OnDestroy {
  @Output() reviewSubmitted: EventEmitter<void> = new EventEmitter<void>();

  @Output() navigateBack: EventEmitter<void> = new EventEmitter<void>();

  subscriptions: Subscription[] = [];
  boondock$: Observable<Boondock | null>;
  boondockId: number | undefined;

  appContext: boolean;

  ratingForm: FormGroup<IBoondockRatingForm>;
  locationRating: number = 0;
  accuracyRating: number = 0;
  overallRating: number = 0;
  user: User;
  schedule: Schedule | null;
  scheduleConfirmationCode: string | undefined;

  constructor(
    private updates$: Actions,
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private navCtrl: NavController,
    private ratingFacade: RatingFacade,
    private scheduleFacade: ScheduleFacade,
    private boondockFacade: BoondockFacade,
    private userFacade: UserFacade,
  ) {}

  ngOnInit(): void {
    this.ratingForm = this.fb.group({
      location: this.fb.group({
        rating: [0, [Validators.required, Validators.min(1), Validators.max(5)]],
        issues: this.fb.group({
          tooNoisy: false,
          photos: false,
          amenities: false,
          address: false,
        }),
        additionalComments: '',
      }),
      accuracy: this.fb.group({
        rating: [0, [Validators.required, Validators.min(1), Validators.max(5)]],
        issues: this.fb.group({
          description: false,
          photos: false,
          amenities: false,
          addressLocation: false,
        }),
        additionalComments: '',
      }),
      overall: this.fb.group({
        rating: [0, [Validators.required, Validators.min(1), Validators.max(5)]],
        issues: this.fb.group({
          difficultToFind: false,
          incorrectInfo: false,
          unclear: false,
        }),
        additionalComments: '',
      }),
    });

    this.boondock$ = this.boondockFacade.selected$;
    this.subscriptions.push(
      this.boondock$.subscribe((boondock) => {
        if (boondock) {
          this.boondockId = boondock.id;
        }
      }),
      this.route.data.subscribe((data: { appContext: boolean }) => {
        this.appContext = data.appContext || false;
      }),
      this.ratingForm.valueChanges.subscribe((newForm) => {
        this.locationRating = newForm.location?.rating || 0;
        this.accuracyRating = newForm.accuracy?.rating || 0;
        this.overallRating = newForm.overall?.rating || 0;
      }),
      this.userFacade.currentUser$.subscribe((user) => {
        this.user = user;
      }),
      this.scheduleFacade.selected$.subscribe((schedule) => {
        this.schedule = schedule;
        this.scheduleConfirmationCode = schedule?.confirmationCode;
      }),
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => {
      if (!sub.closed) {
        sub.unsubscribe();
      }
    });
  }

  async onSubmitReview() {
    this.ratingForm.controls.location.get('rating')?.markAsTouched();
    this.ratingForm.controls.accuracy.get('rating')?.markAsTouched();
    this.ratingForm.controls.overall.get('rating')?.markAsTouched();

    if (this.ratingForm.dirty && this.ratingForm.valid) {
      const ratings: BoondockRating[] = [];
      for (const x in this.ratingForm.value) {
        if (this.ratingForm.value.hasOwnProperty(x)) {
          const rating = (this.ratingForm.value as any)[x];
          const issues: string[] = [];
          if (rating.issues) {
            for (const issueName in rating.issues) {
              if (rating.issues.hasOwnProperty(issueName)) {
                const issue = rating.issues[issueName];
                if (issue === true) {
                  issues.push(issueName);
                }
              }
            }
          }

          const topic: RatingType = x as RatingType;

          ratings.push(
            new BoondockRating({
              topic,
              score: {
                score: rating.rating,
                description: '',
              },
              issues,
              additionalComments: rating.additionalComments,
              creator: this.user,
              creatorId: this.user?.id || 0,
              scheduleCode: this.scheduleConfirmationCode,
              ratedId: this.boondockId,
              rated: { id: this.boondockId },
              created: DateTime.now().toMillis(),
            }),
          );
        }
      }

      // Make the subscription before sending the request to make sure we don't miss anything.
      this.subscriptions.push(
        this.updates$.pipe(ofType(RatingActionTypes.BoondockRatingsCreated)).subscribe(async () => {
          if (this.schedule) {
            // Update the schedule so that it's rated.
            this.scheduleFacade.update({
              ...this.schedule,
              rated: true,
            });
          }

          // Emit event for web and navigate for app.
          if (this.appContext) {
            await this.navCtrl.navigateForward(['rating', 'boondock', this.boondockId, 'review']);
          } else {
            this.reviewSubmitted.emit();
          }
        }),
      );

      this.ratingFacade.createBoondock(ratings);
    }
  }
}
